import axios from '../config/axios';

const API_BASE = '/auth';

const authService = {
  // Login user
  login: async (username, password) => {
    const response = await axios.post(`${API_BASE}/login`, {
      username,
      password
    });
    
    if (response.data.data) {
      // Store user data with token in localStorage
      localStorage.setItem('user', JSON.stringify(response.data.data));
    }
    
    return response.data;
  },

  // Logout user
  logout: async () => {
    try {
      await axios.post(`${API_BASE}/logout`);
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('user');
    }
  },

  // Forgot password - Send OTP
  forgotPassword: async (email) => {
    const response = await axios.post(`${API_BASE}/forgot-password`, {
      email
    });
    return response.data;
  },

  // Reset password with OTP
  resetPassword: async (email, otp, newPassword, confirmPassword) => {
    const response = await axios.post(`${API_BASE}/reset-password`, {
      email,
      otp,
      new_password: newPassword,
      confirm_password: confirmPassword
    });
    return response.data;
  },

  // Change password (for logged-in users)
  changePassword: async (currentPassword, newPassword, confirmPassword) => {
    const response = await axios.post(`${API_BASE}/change-password`, {
      current_password: currentPassword,
      new_password: newPassword,
      confirm_password: confirmPassword
    });
    return response.data;
  },

  // Verify session
  verifySession: async () => {
    try {
      const response = await axios.get(`${API_BASE}/verify-session`);
      return response.data;
    } catch (error) {
      authService.logout();
      return null;
    }
  },

  // Refresh token
  refreshToken: async () => {
    try {
      const response = await axios.post(`${API_BASE}/refresh-token`);
      
      if (response.data.data) {
        // Update user data with new token
        const user = authService.getCurrentUser();
        if (user) {
          user.token = response.data.data.token;
          user.token_expiry = response.data.data.token_expiry;
          localStorage.setItem('user', JSON.stringify(user));
        }
      }
      
      return response.data;
    } catch (error) {
      authService.logout();
      throw error;
    }
  },

  // Get current user from localStorage
  getCurrentUser: () => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        return JSON.parse(userStr);
      } catch (error) {
        console.error('Error parsing user data:', error);
        localStorage.removeItem('user');
        return null;
      }
    }
    return null;
  },

  // Get token
  getToken: () => {
    const user = authService.getCurrentUser();
    return user?.token;
  },

  // Check if user is authenticated
  isAuthenticated: () => {
    const user = authService.getCurrentUser();
    if (!user || !user.token) {
      return false;
    }
    
    // Check token expiry
    try {
      const expiry = new Date(user.token_expiry);
      return expiry > new Date();
    } catch (error) {
      return false;
    }
  },

  // Check if user has specific role
  hasRole: (role) => {
    const user = authService.getCurrentUser();
    return user?.role === role;
  },

  // Check if user has any of the roles
  hasAnyRole: (roles) => {
    const user = authService.getCurrentUser();
    return roles.includes(user?.role);
  },

  // Update user data in localStorage
  updateUserData: (userData) => {
    const currentUser = authService.getCurrentUser();
    if (currentUser) {
      const updatedUser = { ...currentUser, ...userData };
      localStorage.setItem('user', JSON.stringify(updatedUser));
    }
  }
};

export default authService;